import React from 'react';
import Grid from '@material-ui/core/Grid';
import Editor from '../../../../../common/editor/Editor';
import CustomInput from '../../../../../common/input/CustomInput';
import {
  FormHelperText,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';
import { Button } from '../../../../../common/button/Button';
import AssignmentQuestionEditor from './AssignmentQuestionEditor';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import {
  AssignmentPassage,
  AssignmentPassageQuestion
} from '../../../state/assignment-edit.types';
import { useAppSelector } from '../../../../../../../redux/hooks';
import QuestionHelpBar from './QuestionHelpBar';

const useStyles = makeStyles({
  delete: {
    textAlign: 'right'
  }
});

interface AssignmentPassageEditorProps {
  passageData: AssignmentPassage;
  /**
   * function to handle removing a question
   * @param id
   */
  handleDeleteQuestion(id: string | null, rootId?: string): void;
  /**
   * function to handle adding a question
   * @param id
   */
  handleAddQuestionAnswer(id: string, rootId?: string): void;
  /**
   * function to handle data changes
   * @param id
   * @param data
   */
  handleTextChange(id: string, data: object, rootId?: string): void;
  handleAnswerUpdate(
    questionId: string,
    answerId: string,
    data: object,
    rootId?: string
  ): void;
  handleAddQuestionAnswer(id: string, rootId?: string): void;
  handleDeleteAnswer(
    questionId: string,
    answerId: string,
    rootId?: string
  ): void;
  handleQuestionFormat(id: string, format: number, rootId?: string): void;
  handleAddQuestion(rootId?: string): void;
  handleQuestionIndex(id: string, rootId?: string): number;
}

const AssignmentPassageEditor: React.FC<AssignmentPassageEditorProps> = (
  props: AssignmentPassageEditorProps
) => {
  const classes = useStyles();
  const {
    passageData,
    handleDeleteQuestion,
    handleAddQuestionAnswer,
    handleTextChange,
    handleAnswerUpdate,
    handleQuestionFormat,
    handleDeleteAnswer,
    handleAddQuestion,
    handleQuestionIndex
  } = props;

  const { questions, id: rootId, passageTitle, passage, error } = passageData;

  const handleSubDeleteQuestion = (id: string) =>
    handleDeleteQuestion(id, rootId);
  const handleSubAddQuestionAnswer = (id: string) =>
    handleAddQuestionAnswer(id, rootId);
  const handleSubTextChange = (id: string, data: object) =>
    handleTextChange(id, data, rootId);
  const handleSubAnswerUpdate = (
    questionId: string,
    answerId: string,
    data: object
  ) => handleAnswerUpdate(questionId, answerId, data, rootId);
  const handleSubDeleteAnswer = (questionId: string, answerId: string) =>
    handleDeleteAnswer(questionId, answerId, rootId);
  const handleSubQuestionFormat = (id: string, format: number) =>
    handleQuestionFormat(id, format, rootId);
  const handleSubAddQuestion = ({}: any) => handleAddQuestion(rootId);
  const handleSubQuestionIndex = (id: string) =>
    handleQuestionIndex(id, rootId);

  const index = handleQuestionIndex(rootId);

  const genres = useAppSelector(state => state.editAssignment.genres);
  const filteredQuestions = questions.filter(question => !question.isDeleted);
  const questionLength = filteredQuestions.length;

  /**
   * Circumvent error where errors property is turned into object
   */
  if (!(error?.errors instanceof Map)) {
    if (error) {
      error.errors = new Map();
    }
  }
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>
          Passage {index}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <CustomInput
          placeholder={'Title...'}
          value={passageTitle}
          onChange={evt =>
            handleTextChange(passageData.id, { passageTitle: evt.target.value })
          }
          name={'passageTitle'}
        />
        {error?.errors?.get('passageTitle') && (
          <FormHelperText error={true}>
            <b>{error?.errors?.get('passageTitle')?.message}</b>
          </FormHelperText>
        )}
      </Grid>
      <Grid item xs={12}>
        <Editor
          value={passage}
          handleChange={text =>
            handleTextChange(passageData.id, { passage: text })
          }
          placeholder={'Passage...'}
        />
        {error?.errors?.get('passage') && (
          <FormHelperText error={true}>
            <b>{error?.errors?.get('passage')?.message}</b>
          </FormHelperText>
        )}
      </Grid>
      <Grid item xs={12}>
        <Select
          name={'genreId'}
          value={passageData.genreId || -1}
          onChange={evt =>
            handleTextChange(passageData.id, { genreId: evt.target.value })
          }
          input={<CustomInput />}
        >
          <MenuItem value={-1}>-- Select Genre --</MenuItem>
          {genres.map(genre => {
            return (
              <MenuItem key={genre.id} value={genre.id}>
                {genre.name}
              </MenuItem>
            );
          })}
        </Select>
        {error?.errors?.get('genreId') && (
          <FormHelperText error={true}>
            <b>{error?.errors?.get('genreId')?.message}</b>
          </FormHelperText>
        )}
      </Grid>
      <Grid item xs={12}>
        <div
          style={{
            padding: '1rem',
            border: '1px solid lightgrey',
            borderRadius: '4px'
          }}
        >
          {filteredQuestions &&
            filteredQuestions.map((question: AssignmentPassageQuestion) => {
              return (
                <AssignmentQuestionEditor
                  key={question.id}
                  showQuestionFormat={true}
                  handleDeleteAnswer={handleSubDeleteAnswer}
                  handleQuestionFormat={handleSubQuestionFormat}
                  handleDeleteQuestion={handleSubDeleteQuestion}
                  handleAddQuestionAnswer={handleSubAddQuestionAnswer}
                  handleAnswerUpdate={handleSubAnswerUpdate}
                  handleTextChange={handleSubTextChange}
                  handleQuestionIndex={handleSubQuestionIndex}
                  index={index}
                  question={question}
                />
              );
            })}
        </div>
      </Grid>
      <Grid item xs={12}>
        <Grid container>
          <Grid item xs={5}>
            <QuestionHelpBar questionLength={questionLength} />
          </Grid>
          <Grid item>
            <Button
              variant={'filled'}
              color={'green'}
              textColor={'white'}
              icon={Add}
              onClick={handleSubAddQuestion}
            >
              Question
            </Button>
          </Grid>
          <Grid item xs={12} className={classes.delete}>
            <IconButton onClick={() => handleDeleteQuestion(null, rootId)}>
              <Delete />
            </IconButton>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AssignmentPassageEditor;
